import * as Location from 'expo-location';
import { useState, useEffect } from 'react';

interface Coordinates {
	latitude: number;
	longitude: number;
	accuracy?: number | null;
}

interface LocationState {
	location: Coordinates | null;
	error: string | null;
	loading: boolean;
}

/**
 * Custom hook to access device location
 * @returns Current location state with latitude and longitude
 */
export const useLocation = (): LocationState => {
	const [state, setState] = useState<LocationState>({
		location: null,
		error: null,
		loading: true,
	});

	useEffect(() => {
		let isMounted = true;
		(async () => {
			try {
				// Do not prompt automatically; only read existing permission
				const { status } = await Location.getForegroundPermissionsAsync();
				if (status !== 'granted') {
					if (isMounted) setState({ location: null, error: null, loading: false });
					return;
				}
				const position = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
				if (isMounted) {
					setState({
						location: {
							latitude: position.coords.latitude,
							longitude: position.coords.longitude,
							accuracy: position.coords.accuracy ?? null,
						},
						error: null,
						loading: false,
					});
				}
			} catch (error) {
				if (isMounted) setState({ location: null, error: 'Failed to get location', loading: false });
				console.error('Error getting location:', error);
			}
		})();
		return () => { isMounted = false; };
	}, []);

	return state;
};
